import React, { useState, useEffect } from "react";
import { Box, FormControl, InputLabel, Select, MenuItem, Button, TextField, Autocomplete, Typography } from "@mui/material";
import { RegistryService } from "../service/registryService";
import { Server } from './Serverlist';
import { ServerService } from "../service/serverService";

export interface ServerDeploymentDialogContentProps {
  onClose: () => void;
  server: Server | null;
}

interface ConfigElement {
  name: string;
  value: string;
  image: string;
  values: string[];
}

export const ServerDeploymentDialogContent: React.FC<ServerDeploymentDialogContentProps> = ({ server, onClose }) => {
  const initialMap = new Map<string, ConfigElement>([
    ["model-version", { name: "Model Version", value: "-1", image: "model", values: [] }],
    ["predictor-version", { name: "Predictor Version", value: "-1", image: "predictor-ampere", values: [] }],
    ["plcethernet-version", { name: "Plcethernet Version", value: "-1", image: "plcethernet", values: [] }],
    ["playbooks-version", { name: "Playbooks Version", value: "-1", image: "playbook", values: [] }],
    ["playbook", { name: "Playbook", value: "", image: "", values: ["deploy-all.yml", "deploy-greenfield.yml", "deploy-software.yml", "deploy-customer-config.yml", ] }]
  ]);

  const [configMap, setConfigMap] = useState<Map<string, ConfigElement>>(initialMap);
  const [versionEditMode, setVersionEditMode] = useState<boolean>(false);

  const updateConfig = (key: string, updates: Partial<ConfigElement>) => {
    const newMap = new Map(configMap);
    const element = newMap.get(key);

    if (element) {
      newMap.set(key, { ...element, ...updates });
      setConfigMap(newMap);
    }
  };

  const onSaveVersions = async () => {
    setVersionEditMode(false);
    const newDbVrs = {
      [configMap.get("model-version")?.image as string]: configMap.get("model-version")?.value as string,
      [configMap.get("predictor-version")?.image as string]: configMap.get("predictor-version")?.value as string,
      [configMap.get("plcethernet-version")?.image as string]: configMap.get("plcethernet-version")?.value as string,
      [configMap.get("playbooks-version")?.image as string]: configMap.get("playbooks-version")?.value as string,
    }
    await ServerService.updateDeploymentVersions(server ? server.ref : "", newDbVrs);
  };

  const onRunPlaybook = () => {
    setVersionEditMode(false);
    const vars = {
      "playbook-name": configMap.get("playbook")?.value as string,
    }
    ServerService.deployServerV2(server ? server.ref : "", vars);
    onClose();
  };

  useEffect(() => {
    async function fetchTags() {
      try {
        const dbVrs = server?.deploymentVersions;
        const updates = await Promise.all(
          Array.from(configMap.entries())
            .filter(([_, el]) => el.image !== "")
            .map(async ([id, cel]) => {
              const tags = await RegistryService.getTagsForImage(cel.image);
              const sortedTags = tags
              .filter(el => 
                !el.startsWith("buildcache") &&
                !el.endsWith("-arm64") &&
                !el.endsWith("-amd64")
              )
              .sort()
              .reverse();

              // Select first entry in list as default fallback
              let selectedValue = sortedTags[0];
              const dbVal = dbVrs?.[cel.image];
              if (dbVal !== undefined && dbVal.includes(dbVal)) {
                selectedValue = dbVal;
              }
              if (dbVal === undefined || !sortedTags.includes(dbVal)) {
                console.log(dbVal + " not found in tags (" + sortedTags.join(",") + ") for image: " + cel.image);
                // indicate visually that this value does not come from DB
                setVersionEditMode(true);
              }
              return { id, values: sortedTags, value: selectedValue };
            })
        );

        const newMap = new Map(configMap);
        updates.forEach((newProps) => {
          const element = newMap.get(newProps.id);
          if (element) {
            newMap.set(newProps.id, { ...element, ...newProps });
          }
        });
        setConfigMap(newMap);
      } catch (error) {
        console.error("Error in fetchTags:", error);
      }
    }
    fetchTags();
  }, []);

  return (
    <Box sx={{ mb: 4, mt: 4 }}>
      {Array.from(configMap.entries()).map(([id, cel]) => (
        <Box key={id} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            {id !== "playbook" && (
              <FormControl size="small" sx={{ width: "100%" }}>
                <InputLabel>{cel.name}</InputLabel>
                <Select
                  labelId={`select-${id}`}
                  value={cel.value}
                  label={cel.name}
                  onChange={e => updateConfig(id, { value: e.target.value })}
                  disabled={!versionEditMode}
                  sx={{ backgroundColor: versionEditMode ? "inherit" : "#f5f5f5" }}
                >
                  {cel.value === "-1" && (
                    <MenuItem value="-1">Loading values...</MenuItem>
                  )}
                  {cel.values.map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      ))}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="outlined"
          onClick={versionEditMode ? () => onSaveVersions() : () => setVersionEditMode(true)}
          sx={{ backgroundColor: versionEditMode ? "green" : "purple", color: "white" }}>
          {versionEditMode ? "Save Versions" : "Edit Versions"}
        </Button>
      </Box>
      {Array.from(configMap.entries()).map(([id, cel]) => (
        <Box key={id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            {id === "playbook" && (
              <FormControl size="small" sx={{ width: "100%" }}>
                <Autocomplete
                  freeSolo
                  options={cel.values}
                  value={cel.value}
                  size={"small"}
                  onChange={(event, newValue) => {
                    updateConfig(id, { value: newValue as string })
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size={"small"} InputLabelProps={{ shrink: true }} label="Playbook" />
                  )}
                />
              </FormControl>
            )}
          </Box>
        </Box>
      ))}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="outlined"
          disabled={configMap.get("playbook")?.value === "" || versionEditMode}
          onClick={() => onRunPlaybook()}
          sx={{ backgroundColor: configMap.get("playbook")?.value === "" || versionEditMode ? "grey" : "purple", color: "white" }}>
          Run Playbook
        </Button>
      </Box>
      
      {server && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            SSH Verbindungsdaten:
          </Typography>
          <Typography variant="body2">
            <strong>Host:</strong> {server?.ip}
          </Typography>
          <Typography variant="body2">
            <strong>Benutzer:</strong> {server?.user}
          </Typography>
          <Typography variant="body2">
            <strong>Passwort:</strong> {server?.password}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
